import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/shared/lib/supabase";
import {
  readCounterpart,
  type CounterpartFile,
} from "@/platform/playbooks/counterpart";
import type { PlaybookId } from "@/platform/playbooks/types";

export interface Counterpart {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  file: CounterpartFile;
}

interface ClientRow {
  id: string;
  name: string;
  case_data: unknown;
  created_at: string;
  updated_at: string;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Not signed in");
  return { Authorization: `Bearer ${token}` };
}

/** A participant becomes a counterpart once it carries a playbook file. */
export function toCounterpart(row: ClientRow): Counterpart | null {
  const file = readCounterpart(row.case_data);
  return file
    ? {
        id: row.id,
        name: row.name,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        file,
      }
    : null;
}

export async function createCounterpart(
  name: string,
  playbook: PlaybookId,
): Promise<Counterpart> {
  const response = await fetch("/api/clients", {
    method: "POST",
    headers: { ...(await authHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({
      name,
      record_type: "participant",
      counterpart: { playbook, prep: {} },
    }),
  });
  if (!response.ok) throw new Error(`Create failed (${response.status})`);
  const counterpart = toCounterpart((await response.json()).client);
  if (!counterpart) throw new Error("The server did not store the playbook");
  return counterpart;
}

export function useCounterparts() {
  const [counterparts, setCounterparts] = useState<Counterpart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await fetch("/api/clients?recordType=participant", {
        headers: await authHeaders(),
      });
      if (!response.ok) throw new Error(`List failed (${response.status})`);
      const rows = ((await response.json()).clients ?? []) as ClientRow[];
      setCounterparts(
        rows
          .map(toCounterpart)
          .filter((item): item is Counterpart => !!item)
          .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
      );
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { counterparts, loading, error, retry: load };
}

export function useCounterpart(id: string) {
  const [counterpart, setCounterpart] = useState<Counterpart | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing" | "error">(
    "loading",
  );

  const load = useCallback(async () => {
    setState("loading");
    try {
      const response = await fetch(`/api/clients/${encodeURIComponent(id)}`, {
        headers: await authHeaders(),
      });
      if (!response.ok) {
        // The endpoint answers 500 for a row that isn't the caller's.
        setState(response.status === 401 ? "error" : "missing");
        return;
      }
      const next = toCounterpart((await response.json()).client);
      setCounterpart(next);
      setState(next ? "ready" : "missing");
    } catch {
      setState("error");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const savePrep = useCallback(
    async (prep: Record<string, string>) => {
      const response = await fetch(`/api/clients/${encodeURIComponent(id)}`, {
        method: "PUT",
        headers: {
          ...(await authHeaders()),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ counterpart: { prep } }),
      });
      if (!response.ok) throw new Error(`Save failed (${response.status})`);
      const next = toCounterpart((await response.json()).client);
      if (!next) throw new Error("The server dropped the playbook");
      setCounterpart(next);
      return next;
    },
    [id],
  );

  return { counterpart, state, retry: load, savePrep };
}
